import React, { FunctionComponent } from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import AppState from '@webapp/store/state';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import { ListReleases } from '@webapp/store/release/action-creators';

import Box from '@components/layout/box';
import Grid from '@components/basics/grid';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import SquareCover from '@components/basics/square-cover';
import Percentage from '@components/dashboard/metrics-widget/percentage';
import { GetArtistAnalytics } from '@webapp/store/artist-analytics/action-creators';
import { AnalyticsQueryTypes } from '@services/analytics/types/analytics-query-enum';

interface TopSongsBoxProps {
  className?: string;
  topSongs: {
    id: string;
    title: string;
    url: string;
    streams: number;
    change: number;
    percentChange: number;
  }[];
  artistId?: string;
  getReleases: () => ListReleases;
  isMobile: boolean;
  getTopSongsAnalytics: () => GetArtistAnalytics;
}

const mapStateToProps = (state: AppState) => {
  const sampleSongs = (state.release.summary?.all?.list || [])
    .slice(0, 3)
    .map((release, i) => {
      return {
        id: release.id,
        title: release.title,
        url: release.cover.url,
        streams: 0,
        change: (i + 1) * 123,
        percentChange: (i + 1) * 1.6,
      };
    });
  return {
    topSongs: !state.artistAnalytics.isExample
      ? state.artistAnalytics.data?.topSongs
          .map((song) => {
            const catalog = state.catalog.master.items.find((r) => {
              const track =
                r.tracks.filter((t) => t.isrc === song.category) || [];
              return track.length > 0;
            });
            return {
              id: catalog?.id,
              title: catalog?.tracks.find((t) => t.isrc === song.category)
                ?.name,
              url: catalog?.cover_url,
              streams: song.value,
              change: song.value,
              percentChange: song.percentChange,
            };
          })
          .filter((item) => item.id !== undefined)
          .sort((a, b) => b.streams - a.streams)
          .slice(0, 5) || []
      : sampleSongs,
    artistId: state.artist.active?.id,
    isMobile: state.ui.mobile,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getReleases: () => dispatch(new ListReleases('all')),
  getTopSongsAnalytics: () =>
    dispatch(
      new GetArtistAnalytics(AnalyticsQueryTypes.SONGS, undefined, true)
    ),
});

const TopSongsBox: FunctionComponent<TopSongsBoxProps> = ({
  className,
  topSongs,
  artistId,
  getReleases,
  getTopSongsAnalytics,
  isMobile,
}) => {
  React.useEffect(() => {
    if (artistId) {
      getTopSongsAnalytics();
    }
  }, [artistId, getTopSongsAnalytics]);

  React.useEffect(() => {
    if (artistId) {
      getReleases();
    }
  }, [artistId, getReleases]);

  const normalFormatter = Intl.NumberFormat('en-us', {});

  return (
    <Box
      className={className || ''}
      noBorderTop
      noBorderBottom={isMobile}
      noBorderRight={!isMobile}
    >
      <Typography className="title" type="title" customFont="semi-bold">
        <Translate id="ANALYTICS.ENGAGEMENT.TOP_SONGS.TITLE" />
      </Typography>
      <Grid gap={20}>
        {topSongs
          .sort((a, b) => b.streams - a.streams)
          .map((song, i) => (
            <Flex
              key={`${song.id}-${song.title}-${i}`}
              flexDirection={isMobile ? 'column' : 'row'}
              justifyContent={isMobile ? 'flex-start' : 'space-between'}
              alignItems={isMobile ? 'flex-start' : 'center'}
              gap={isMobile ? 10 : 20}
            >
              <Flex justifyContent="flex-start" alignItems="center" gap={10}>
                <SquareCover
                  className="cover"
                  src={song.url}
                  alt={song.title}
                  borderRadius={4}
                  size={32}
                />
                <Grid gap={2}>
                  <Typography customFont="semi-bold">{song.title}</Typography>
                  <Typography isGrey type="small">
                    {/*<Translate
                    id="ANALYTICS.AMOUNT.LISTENERS"
                    data={{ amount: normalFormatter.format(song.streams) }}
                  />*/}
                  </Typography>
                </Grid>
              </Flex>
              <Flex
                justifyContent="flex-end"
                alignItems="center"
                gap={4}
                flexShrink="0"
                style={{ marginLeft: isMobile ? '42px' : '0' }}
              >
                <Typography weight="700" type="small">
                  <Translate
                    id="ANALYTICS.AMOUNT.STREAMS"
                    data={{ amount: normalFormatter.format(song.change) }}
                  />
                </Typography>
                <Percentage
                  noBackground
                  value={song.percentChange}
                  valueFormat="percentage"
                />
              </Flex>
            </Flex>
          ))}
      </Grid>
    </Box>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(TopSongsBox)`
  .title {
    margin-bottom: 20px;
  }

  .cover {
    flex-shrink: 0;
  }
`);
